// React
import { useContext, useEffect } from "react";
import PropTypes from "prop-types";

// React Query
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Redux
import { useSelector } from "react-redux";

// MUI
import { Grid, Typography } from "@mui/material";

// Third-party
import moment from "moment";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Context
import { useMotivators } from "@/Motivators/context/Common/MotivatorsContext";

// Components
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import TrainingSectionTable from "../Tables/TrainingSectionTable";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import SpiffEntrySectionLoader from "@/Motivators/layouts/components/Skeletons/SpiffEntrySectionLoader";

// Images
import empty from "@/assets/images/commons/empty.png";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

export default function TrainingSection(props) {
	const location = useSelector((state) => state.location.location);
	const queryClient = useQueryClient();

	const { queries, motivatorType } = useMotivators();

	const QUERY_IDENTIFIERS = [motivatorType + "Entry", "serviceTraining", "userTrainingInput"];
	const { clearCache } = useCacheCleaner();

	const { data: dataTable, isLoading: isLoadingTraining } = useQuery({
		queryKey: ["serviceTraining", props?.selectedItem?._id, location?.locationId],
		queryFn: async () => queries.getBodyData(location.locationId, props?.selectedItem?._id),
		enabled: !!location?.locationId && !!props?.selectedItem?._id,
		staleTime: Infinity,
	});

	// Clean up cache
	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [location?.locationId, props.subscriptions, queryClient]);

	const titlePrefix = props?.selectedItem?.title ? `${props?.selectedItem?.title} Training` : "Training";

	const correctTimezone = (date) => moment(date, DATE_FORMAT.isoWithMillis);

	const renderContent = () => {
		if (!props?.selectedItem) {
			return <EmptyBody image={String(empty)} title={"No Item selected"} bodyHeight={"510px"} />;
		}

		if (isLoadingTraining) {
			return <SpiffEntrySectionLoader entry />;
		}

		return (
			<Grid container>
				<Grid item xs={12} alignItems={"center"}>
					<Typography className={"general-section-title"}>{titlePrefix}</Typography>
				</Grid>
				<Grid item xs={12}>
					<TrainingSectionTable
						key={props.selectedItem?._id}
						data={dataTable?.items || []}
						loading={isLoadingTraining}
						locationId={location.locationId}
						dataPresent={Boolean(props.selectedItem)}
						currentMonth={props.selectedItem?.status === "ACTIVE" || props.selectedItem?.status === "CLOSING"}
						commissionCycleId={props.selectedItem?._id}
						effectiveDateRangeFrom={correctTimezone(props.selectedItem?.effectiveDateRangeFrom)}
						effectiveDateRangeTo={correctTimezone(props.selectedItem?.effectiveDateRangeTo)}
					/>
				</Grid>
			</Grid>
		);
	};

	return (
		<RoundedBox
			sx={{
				minHeight: "630px",
			}}
			className={"!pb-[48px]"}
		>
			{renderContent()}
		</RoundedBox>
	);
}

TrainingSection.propTypes = {
	selectedItem: PropTypes.shape({
		_id: PropTypes.string.isRequired,
		effectiveDateRangeFrom: PropTypes.string.isRequired,
		effectiveDateRangeTo: PropTypes.string.isRequired,
		status: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
	}),
	subscriptions: PropTypes.array,
};
